import React, { useState, useEffect, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import api from '../../services/api';

const POLL_MS = 4000;

const MP4_STATUS_LABEL = {
  pending: 'Queued…',
  processing: 'Rendering…',
};

export default function VideoExportButton({ videoId, videoTitle, compact = false }) {
  const [open, setOpen] = useState(false);

  // MP4 job state
  const [jobId,          setJobId]          = useState(null);
  const [mp4Status,      setMp4Status]      = useState(null); // null|pending|processing|ready|failed|expired
  const [mp4Error,       setMp4Error]       = useState('');
  const [mp4Starting,    setMp4Starting]    = useState(false);
  const [mp4Downloading, setMp4Downloading] = useState(false);

  // PPTX state
  const [pptxBusy,  setPptxBusy]  = useState(false);
  const [pptxError, setPptxError] = useState('');

  const pollRef  = useRef(null);
  const menuRef  = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  };
  useEffect(() => () => stopPolling(), []);

  // Notify when export finishes
  useEffect(() => {
    if (mp4Status === 'ready') {
      toast.success('MP4 ready — click to download!', {
        duration: 8000,
        position: 'top-right',
        icon: '🎞',
      });
    } else if (mp4Status === 'failed') {
      toast.error('MP4 export failed. Please retry.', {
        duration: 6000,
        position: 'top-right',
      });
    }
  }, [mp4Status]);

  // ── MP4 ──────────────────────────────────────────────────────────────────
  const pollJob = async (id) => {
    try {
      const { data } = await api.get(`/videos/export/${id}`);
      setMp4Status(data.status);
      if (['ready', 'failed', 'expired'].includes(data.status)) {
        stopPolling();
        if (data.error) setMp4Error(data.error);
      }
    } catch { /* ignore transient */ }
  };

  const startMp4Export = async () => {
    setMp4Starting(true);
    setMp4Error('');
    try {
      const { data } = await api.post(`/videos/${videoId}/export`);
      setJobId(data.job_id);
      setMp4Status(data.status);
      if (data.status !== 'ready') {
        pollRef.current = setInterval(() => pollJob(data.job_id), POLL_MS);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      setMp4Error(typeof msg === 'string' ? msg : 'Export failed. Please try again.');
    } finally {
      setMp4Starting(false);
    }
  };

  const downloadMp4 = async () => {
    if (!jobId || mp4Downloading) return;
    setMp4Downloading(true);
    try {
      const response = await api.get(`/videos/export/${jobId}/download`, {
        responseType: 'blob',
        timeout: 300000,
      });
      const blob = new Blob([response.data], { type: 'video/mp4' });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${(videoTitle || 'video').replace(/\s+/g, '_')}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch {
      // Mark as failed so the error is visible and the user can re-export
      setMp4Error('Download failed — export may have expired. Click retry to generate a fresh copy.');
      setMp4Status('failed');
      setJobId(null);
    } finally {
      setMp4Downloading(false);
    }
  };

  const resetMp4 = async () => {
    stopPolling();
    if (jobId) { try { await api.delete(`/videos/export/${jobId}`); } catch { /* ok */ } }
    setJobId(null); setMp4Status(null); setMp4Error('');
  };

  // ── PPTX ─────────────────────────────────────────────────────────────────
  const downloadPptx = async () => {
    setPptxBusy(true);
    setPptxError('');
    setOpen(false);
    try {
      const response = await api.get(`/videos/${videoId}/export/pptx`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] ||
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${(videoTitle || 'video').replace(/\s+/g, '_')}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch (e) {
      let msg = e?.response?.data?.detail || e.message || 'PPTX export failed';
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { /* ok */ }
      }
      setPptxError(typeof msg === 'string' ? msg : 'PPTX export failed');
    } finally {
      setPptxBusy(false);
    }
  };
  // ── Render ────────────────────────────────────────────────────────────────
  const mp4Active = mp4Status === 'pending' || mp4Status === 'processing';
  const mp4Ready  = mp4Status === 'ready';

  return (
    <div className="relative" ref={menuRef}>
      <Toaster />
      {/* Main trigger button */}
      <button
        onClick={() => setOpen((p) => !p)}
        className={compact
          ? 'compact-action-btn px-2 py-1 sm:px-4 sm:py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-xs flex items-center gap-1'
          : 'flex items-center gap-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs sm:text-sm font-semibold px-2 py-1 sm:px-4 sm:py-2 rounded-lg transition-colors'}
        title="Export this video"
      >
        {compact ? (
          <>
            <span className="text-xs text-white inline sm:hidden">▾</span>
            <span className="text-xs text-white hidden sm:inline">Export ▾</span>
          </>
        ) : (
          <>Export ▾</>
        )}
        {mp4Ready && (
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" title="MP4 ready to download" />
        )}
        {mp4Active && (
          <span className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" title="MP4 rendering…" />
        )}
      </button>

      {/* Status banners (shown when dropdown is closed) */}
      {!open && (
        <div className="absolute right-0 top-full mt-1 flex flex-col gap-1 z-40">
          {mp4Ready && (
            <button
              onClick={downloadMp4}
              disabled={mp4Downloading}
              className="flex items-center gap-2 bg-green-50 border border-green-300 text-green-800 text-xs rounded-lg px-3 py-1.5 shadow whitespace-nowrap hover:bg-green-100 disabled:opacity-70 transition-colors"
            >
              {mp4Downloading ? (
                <>
                  <span className="animate-spin inline-block w-2.5 h-2.5 border-2 border-green-600 border-t-transparent rounded-full" />
                  Downloading…
                </>
              ) : (
                <><span>✅</span> MP4 ready — click to download</>
              )}
            </button>
          )}
          {pptxBusy && (
            <div className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 text-xs rounded-lg px-3 py-1.5 shadow whitespace-nowrap">
              <span className="animate-spin inline-block w-2.5 h-2.5 border-2 border-gray-500 border-t-transparent rounded-full" />
              Generating PPTX…
            </div>
          )}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Export Options</p>
          </div>

          {/* PPTX row */}
          <button
            onClick={downloadPptx}
            disabled={pptxBusy}
            className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
          >
            <span className="text-xl mt-0.5">📊</span>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {pptxBusy ? 'Generating…' : 'Export PPTX'}
              </p>
              <p className="text-xs text-gray-500">Editable PowerPoint with slides, bullets &amp; diagrams</p>
              {pptxError && <p className="text-xs text-red-500 mt-0.5">{pptxError}</p>}
            </div>
          </button>

          <div className="border-t" />

          {/* MP4 row */}
          {mp4Ready ? (
            <button
              onClick={() => { downloadMp4(); setOpen(false); }}
              disabled={mp4Downloading}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-green-50 transition-colors text-left disabled:opacity-50"
            >
              <span className="text-xl mt-0.5">🎞</span>
              <div>
                <p className="text-sm font-semibold text-green-700">
                  {mp4Downloading ? 'Downloading…' : 'Download MP4'}
                </p>
                <p className="text-xs text-gray-500">Video with narration audio (ready)</p>
              </div>
            </button>
          ) : mp4Active ? (
            <div className="flex items-start gap-3 px-4 py-3 opacity-70">
              <span className="text-xl mt-0.5">🎞</span>
              <div>
                <p className="text-sm font-semibold text-gray-600 flex items-center gap-1.5">
                  <span className="animate-spin inline-block w-3 h-3 border-2 border-gray-500 border-t-transparent rounded-full" />
                  {MP4_STATUS_LABEL[mp4Status]}
                </p>
                <p className="text-xs text-gray-400">Rendering your video in the background…</p>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { startMp4Export(); setOpen(false); }}
              disabled={mp4Starting}
              className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left disabled:opacity-50"
            >
              <span className="text-xl mt-0.5">🎞</span>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {mp4Starting ? 'Queuing…' : 'Export MP4'}
                </p>
                <p className="text-xs text-gray-500">Video with narration audio (background job)</p>
                {mp4Status === 'failed' && (
                  <p className="text-xs text-red-500">
                    {mp4Error || 'Failed'} —{' '}
                    <span
                      className="underline cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); resetMp4(); }}
                    >
                      retry
                    </span>
                  </p>
                )}
                {mp4Status === 'expired' && (
                  <p className="text-xs text-gray-400">Previous export expired — click to re-export</p>
                )}
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
